import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../../../util/API';
import AdminPageShell from '../components/AdminPageShell';
import ADMIN_PATHS from '../ADMIN_PATHS';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SelectBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const FieldBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors',
      className
    )}
  />
);

function AdminView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    phone: '',
    designation: '',
    profilePic: '',
  });
  const [profileFile, setProfileFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirm: '',
  });

  const currentRoleLabel = useMemo(() => {
    if (admin?.roleId?.name) return admin.roleId.name;
    return 'Unassigned';
  }, [admin]);

  const load = async () => {
    setError('');
    setOk('');
    setLoading(true);
    try {
      const [adminRes, rolesRes] = await Promise.all([
        API.get(`/admin/${id}`),
        API.get('/role'),
      ]);
      const adminData = adminRes?.data?.data;
      const rolesData = rolesRes?.data?.data || [];
      setAdmin(adminData);
      setRoles(rolesData);
      setRoleId(adminData?.roleId?._id || '');
      setProfileForm({
        username: adminData?.username || '',
        email: adminData?.email || '',
        phone: adminData?.phone || '',
        designation: adminData?.designation || '',
        profilePic: adminData?.profilePic || '',
      });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    setError('');
    setOk('');
    setSaving(true);
    try {
      await API.put(`/admin/${id}`, {
        roleId: roleId || null,
        username: profileForm.username,
        email: profileForm.email,
        phone: profileForm.phone,
        designation: profileForm.designation,
        profilePic: profileForm.profilePic,
      });
      setOk('Admin profile updated successfully');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update admin');
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    setError('');
    setOk('');
    if (!passwordForm.password || passwordForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await API.put(`/admin/${id}`, { password: passwordForm.password });
      setOk('Password updated successfully');
      setPasswordForm({ password: '', confirm: '' });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const uploadProfilePhoto = async () => {
    if (!profileFile || !id) return;
    setError('');
    setOk('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', profileFile);
      const res = await API.post(`/admin/${id}/profile-pic`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const nextUrl = res?.data?.data?.profilePic;
      if (nextUrl) {
        setProfileForm((prev) => ({ ...prev, profilePic: nextUrl }));
      }
      setOk('Profile photo uploaded successfully');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to upload profile photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminPageShell
      title="Admin View"
      description="View admin details and manage their role."
      actions={
        <SecondaryButton type="button" onClick={() => navigate(ADMIN_PATHS.ADMIN.BASE)}>
          Back to list
        </SecondaryButton>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : admin ? (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Admin</div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {admin.username || 'ADMIN'}
                </div>
                <div className="text-sm text-slate-600">{admin.email}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Name</label>
                  <input
                    className={FieldBase}
                    value={profileForm.username}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="Admin name"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Email</label>
                  <input
                    className={FieldBase}
                    value={profileForm.email}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="Email"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Phone</label>
                  <input
                    className={FieldBase}
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Designation</label>
                  <input
                    className={FieldBase}
                    value={profileForm.designation}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, designation: e.target.value }))}
                    placeholder="Designation"
                    disabled={loading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Profile Picture URL</label>
                  <input
                    className={FieldBase}
                    value={profileForm.profilePic}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, profilePic: e.target.value }))}
                    placeholder="https://..."
                    disabled={loading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Upload Profile Photo</label>
                  <div className="mt-2 flex flex-col md:flex-row md:items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setProfileFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-slate-600"
                      disabled={uploading || loading}
                    />
                    <PrimaryButton type="button" onClick={uploadProfilePhoto} disabled={uploading || loading || !profileFile}>
                      {uploading ? 'Uploading…' : 'Upload'}
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Current Role
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{currentRoleLabel}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Status</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {admin.isActive === false ? 'Disabled' : 'Active'}
                  </div>
                </div>
              </div>

              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                  {error}
                </div>
              ) : null}
              {ok ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  {ok}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-500">Admin not found.</div>
          )}
        </Card>

        <Card className="p-5">
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Assign Role</div>
          <div className="mt-2">
            <select
              className={SelectBase}
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={loading || !admin}
            >
              <option value="">Unassigned</option>
              {roles.map((r) => (
                <option key={r._id} value={r._id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <PrimaryButton type="button" onClick={save} disabled={saving || loading || !admin}>
              {saving ? 'Saving…' : 'Save'}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={load} disabled={saving}>
              Refresh
            </SecondaryButton>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Permissions are enforced for role/admin management APIs.
          </p>
        </Card>

        <Card className="p-5 lg:col-span-3">
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Update Password</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">New Password</label>
              <input
                type="password"
                className={FieldBase}
                value={passwordForm.password}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
                disabled={saving || loading}
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Confirm Password</label>
              <input
                type="password"
                className={FieldBase}
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                placeholder="Repeat new password"
                disabled={saving || loading}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <PrimaryButton type="button" onClick={updatePassword} disabled={saving || loading || !admin}>
              Update Password
            </PrimaryButton>
          </div>
        </Card>
      </div>
    </AdminPageShell>
  );
}

export default AdminView;
